/**
 * usePermission.tsx
 *
 * React hook for runtime feature-level permission checks.
 * Reads from the Zustand permission store and provides helper functions.
 *
 * Feature code format: blx:{route}:{feature}:{action} (exactly 4 segments).
 * Auto-prefixing: if a plugin calls hasFeature("Posts:view") without the "blx:" prefix,
 * shellcore will automatically resolve it to "blx:{currentShellRoute}:Posts:view" using
 * the currently active plugin's shellRoute from the URL (e.g., "cms-local").
 * Plugins can also call buildFeatureCode("Posts", "view", "cms-local") for explicit control.
 *
 * Usage:
 *   const { hasFeature, canManage, isAdminAtLeast } = usePermission();
 *   if (hasFeature('blx:cms-local:Posts:view')) { ... }   // exact code — works as-is
 *   if (hasFeature('Posts:view')) { ... }                  // auto-prefixed to blx:{currentShellRoute}:Posts:view
 *   hasFeature('Posts')                                     // ❌ rejected — missing action suffix
 */

import { useCallback, useRef } from 'react';
import { usePermissionStore } from '../store/permissionStore';

/** Build a full feature code from a local permission name, action, and optional shell route. */
export function buildFeatureCode(localName: string, action: string, shellRoute?: string): string {
  const route = shellRoute ?? usePermissionStore.getState().currentShellRoute;
  if (route) return `blx:${route}:${localName}:${action}`;
  // No route available — caller must pass the full blx-prefixed code.
  return localName.startsWith('blx:') ? `${localName}:${action}` : `blx:${localName}:${action}`;
}

export function usePermission(): {
  /** Check if a global feature code is enabled across all companies. Auto-prefixes if no "blx:" prefix. Must include action suffix (e.g., "Posts:view"). */
  hasFeature: (featureCode: string) => boolean;

  /** Check if the user can perform an action on a specific company's plugin. Defaults to currentCompanyId if not provided. Must include action suffix. */
  canManage: ({ companyId, featureCode }: { companyId?: string; featureCode: string }) => boolean;

  /** Check if admin level meets or exceeds a threshold. */
  isAdminAtLeast: (level: number) => boolean;

  /** Check if the user has ANY permission assigned. Returns true if user has at least one permission, false if none. */
  hasPermissionAssign: boolean;

  /** The current JWT claims from the store. */
  jwtClaims: ReturnType<typeof usePermissionStore.getState>['jwtClaims'];

  /** The currently active company ID. */
  currentCompanyId: string | null;

  /** Whether permission data has been loaded at least once (distinguishes loading vs empty). */
  permissionsLoaded: boolean;

  /** The shellRoute of the currently loaded plugin (for debugging / explicit buildFeatureCode calls). */
  currentShellRoute: string | null;

  /** List all enabled feature codes for the current company's active plugin (uses currentShellRoute). */
  listPermissions: () => string[];

  /** List all enabled feature codes filtered by a specific module name. Delegates to store method. */
  listPermissionsForModule: (moduleName: string) => string[];
} {
  // Subscribe to individual store slices — avoids creating a new object reference
  // on every selector callback, which would trigger unnecessary re-renders.
  const jwtClaims = usePermissionStore((s) => s.jwtClaims);
  const currentCompanyId = usePermissionStore((s) => s.currentCompanyId);
  const hasFeatureFn = usePermissionStore((s) => s.hasFeature);
  const canManageFn = usePermissionStore((s) => s.canManage);
  const isAdminAtLeastFn = usePermissionStore((s) => s.isAdminAtLeast);
  const permissionsLoaded = usePermissionStore((s) => s.permissionsLoaded);
  const currentShellRoute = usePermissionStore((s) => s.currentShellRoute);
  const listPermissionsFn = usePermissionStore((s) => s.listPermissions);
  const hasPermissionAssignFn = usePermissionStore((s) => s.hasPermissionAssign);

  // Stable ref to store's listPermissionsForModule — avoids re-creating the function on every render.
  const listPermissionsForModuleRef = useRef(
    usePermissionStore.getState().listPermissionsForModule,
  );

  return {
    hasFeature: useCallback(
      (featureCode: string): boolean => hasFeatureFn(featureCode),
      [hasFeatureFn],
    ),

    canManage: useCallback(
      ({ companyId, featureCode }: { companyId?: string; featureCode: string }): boolean =>
        canManageFn({ companyId, featureCode }),
      [canManageFn],
    ),

    isAdminAtLeast: useCallback(
      (level: number): boolean => isAdminAtLeastFn(level),
      [isAdminAtLeastFn],
    ),

    hasPermissionAssign: hasPermissionAssignFn(),

    jwtClaims,
    currentCompanyId,
    permissionsLoaded,
    currentShellRoute,
    listPermissions: useCallback(() => listPermissionsFn(), [listPermissionsFn]),
    // Delegate to store method — avoids duplicating filtering logic that already exists in permissionStore.ts.
    listPermissionsForModule: useCallback(
      (moduleName: string): string[] => listPermissionsForModuleRef.current(moduleName),
      [],
    ),
  };
}
